import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, LoadingOverlay, Modal, Stack, Text } from '@mantine/core';

import {
  useDeleteOrganisationLogo,
  usePostOrganisationLogo,
} from 'api/organisations';
import { FileDropzone } from 'components/shared';
import { useShowNotification } from 'hooks';
import type { OrganisationType } from 'types';

type Props = {
  opened: boolean;
  onClose: () => void;
  organisation: OrganisationType;
};

export const LogoSettingsModal = ({
  opened,
  onClose,
  organisation: { id },
}: Props) => {
  const { mutateAsync: postOrganisationLogo } = usePostOrganisationLogo({
    organisationId: id,
  });
  const { mutateAsync: deleteOrganisationLogo } = useDeleteOrganisationLogo({
    organisationId: id,
  });
  const showNotification = useShowNotification();
  const { t } = useTranslation('organisations');
  const [actionPending, setActionPending] = useState(false);

  const onFileDrop = async (file: File) => {
    setActionPending(true);
    try {
      await postOrganisationLogo({ logo: file });
      showNotification({
        message: t(
          'organisationView.details.profile.logo.updateSuccessMessage',
        ),
        variant: 'success',
      });
      onClose();
    } catch (error) {
      showNotification({
        message: t('organisationView.details.profile.logo.updateErrorMessage'),
        variant: 'error',
      });
    }
    setActionPending(false);
  };

  const onDeleteLogo = async () => {
    setActionPending(true);
    try {
      await deleteOrganisationLogo();
      showNotification({
        message: t(
          'organisationView.details.profile.logo.deleteSuccessMessage',
        ),
        variant: 'success',
      });
      onClose();
    } catch (error) {
      showNotification({
        message: t('organisationView.details.profile.logo.deleteErrorMessage'),
        variant: 'error',
      });
    }
    setActionPending(false);
  };

  return (
    <Modal
      size="lg"
      centered
      opened={opened}
      onClose={onClose}
      title={t('organisationView.details.profile.logo.modal.title')}
    >
      <Stack spacing="lg" sx={{ position: 'relative' }}>
        <LoadingOverlay visible={actionPending} overlayOpacity={1} />
        <FileDropzone
          error=""
          setError={() => {}}
          onFileDrop={onFileDrop}
          isLoading={false}
          accept={['image/jpeg', 'image/png']}
          acceptText="JPEG, PNG"
        />
        <Text ta="center">
          {t('organisationView.details.profile.logo.modal.divider')}
        </Text>
        <Button color="red" onClick={onDeleteLogo}>
          {t('organisationView.details.profile.logo.modal.deleteButton')}
        </Button>
      </Stack>
    </Modal>
  );
};
